import { useState, useEffect } from 'react';
import { FileText, Newspaper, Headphones, Youtube, Globe, Loader2 } from 'lucide-react';
import { LibraryResourceType } from '@/types/database';

interface ResourceImageProps {
  src: string | null;
  alt: string;
  type: LibraryResourceType;
  priority?: boolean;
}

const getTypeIcon = (type: LibraryResourceType) => {
  switch (type) {
    case 'pdf':
      return <FileText className="h-8 w-8 text-red-500" />;
    case 'article':
      return <Newspaper className="h-8 w-8 text-blue-500" />;
    case 'podcast':
      return <Headphones className="h-8 w-8 text-purple-500" />;
    case 'video':
      return <Youtube className="h-8 w-8 text-red-600" />;
    case 'web':
      return <Globe className="h-8 w-8 text-green-500" />;
  }
};

export function ResourceImage({ src, alt, type, priority = false }: ResourceImageProps) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);

  useEffect(() => {
    // Reset states when src changes
    setLoaded(false);
    setError(false);
    setImageSrc(null);

    if (!src) return;

    // Create new Image object
    const img = new Image();
    
    img.onload = () => {
      setImageSrc(src);
      setLoaded(true);
    };

    img.onerror = () => {
      setError(true);
      setLoaded(true);
    };

    // Start loading the image
    img.src = src;

    // Clean up
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src]);

  if (!src || error) {
    return (
      <div className="w-full h-48 bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
        {getTypeIcon(type)}
      </div>
    );
  }

  return (
    <div className="relative w-full h-48 bg-gray-100 dark:bg-gray-700">
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="h-8 w-8 text-gray-400 dark:text-gray-500 animate-spin" />
        </div>
      )}
      {imageSrc && (
        <img
          src={imageSrc}
          alt={alt}
          className={`w-full h-full object-cover transition-opacity duration-300 ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
        />
      )}
    </div>
  );
}